/* eslint-disable max-lines */
import {
  useABFeedback,
  useAlarm,
  useAlarmMedia,
  useInferenceMedia,
} from '@hakimo-ui/hakimo/data-access';
import { AlarmMedia, LocationAlarm } from '@hakimo-ui/hakimo/types';
import { Button, HakimoSpinner, Label } from '@hakimo-ui/shared/ui-base';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCallback, useMemo, useState } from 'react';

dayjs.extend(utc);

interface RecordedFeedback {
  bounding_boxes_same: boolean;
  tags_same: boolean;
}

interface Props {
  locationAlarm: LocationAlarm;
  /**
   * When true, disables feedback submission and shows pre-recorded feedback.
   * Used in the AB Testing feedback review app.
   */
  readOnly?: boolean;
  /**
   * Pre-recorded feedback data to display when in read-only mode.
   * Only used when readOnly is true.
   */
  recordedFeedback?: RecordedFeedback;
  /**
   * Optional callback to go back to the list view.
   * Only used in the AB Testing feedback review app.
   */
  onBack?: () => void;
}

interface ComparisonData {
  alarmId: string;
  currentMedia: AlarmMedia | undefined;
  currentMediaLoading: boolean;
  currentTags: string[];
  currentTagsLoading: boolean;
  tritonVideoUrl: string | undefined;
  tritonTags: string[];
}

function AlarmComparisonContent({
  data,
  isSubmitted,
  isSubmitting,
  onSubmit,
  readOnly = false,
  recordedFeedback,
}: {
  data: ComparisonData;
  isSubmitted: boolean;
  isSubmitting: boolean;
  onSubmit: (alarmId: string, boxesSame: boolean, tagsSame: boolean) => void;
  readOnly?: boolean;
  recordedFeedback?: RecordedFeedback;
}) {
  const [boxesSame, setBoxesSame] = useState<boolean | null>(null);
  const [tagsSame, setTagsSame] = useState<boolean | null>(null);

  // Use recorded feedback values in read-only mode
  const displayBoxesSame = readOnly
    ? recordedFeedback?.bounding_boxes_same ?? null
    : boxesSame;
  const displayTagsSame = readOnly
    ? recordedFeedback?.tags_same ?? null
    : tagsSame;

  const handleSubmit = () => {
    if (boxesSame !== null && tagsSame !== null) {
      onSubmit(data.alarmId, boxesSame, tagsSame);
    }
  };

  const canSubmit = boxesSame !== null && tagsSame !== null && !isSubmitted;

  if (data.currentMediaLoading || data.currentTagsLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <HakimoSpinner />
      </div>
    );
  }

  if (!data.tritonVideoUrl) {
    return (
      <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 flex h-96 items-center justify-center rounded-lg">
        <p className="text-onlight-text-2 dark:text-ondark-text-2">
          No inference data available for this alarm
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alarm ID info */}
      <div className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
        Raw Alarm ID: <span className="font-mono">{data.alarmId}</span>
      </div>

      {/* Side-by-side video comparison */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Current System Panel */}
        <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 border-onlight-line-1 dark:border-ondark-line-1 overflow-hidden rounded-lg border">
          <div className="bg-onlight-bg-2 dark:bg-ondark-bg-2 border-onlight-line-1 dark:border-ondark-line-1 border-b px-4 py-3">
            <h3 className="text-onlight-text-1 dark:text-ondark-text-1 font-medium">
              ML Service
            </h3>
          </div>
          <div className="p-4">
            <div className="bg-onlight-bg-3 dark:bg-ondark-bg-3 relative aspect-video overflow-hidden rounded-lg">
              {data.currentMedia?.videoPath ? (
                <video
                  className="h-full w-full object-contain"
                  controls
                  preload="auto"
                  key={data.currentMedia.videoPath}
                  autoPlay
                  loop
                >
                  <source src={data.currentMedia.videoPath} type="video/mp4" />
                </video>
              ) : (
                <div className="text-onlight-text-2 dark:text-ondark-text-2 flex h-full items-center justify-center">
                  No video available
                </div>
              )}
            </div>
            <div className="mt-3">
              <span className="text-onlight-text-2 dark:text-ondark-text-2 mb-2 block text-xs font-medium">
                Tags
              </span>
              <div className="flex flex-wrap gap-1">
                {data.currentTags.length > 0 ? (
                  data.currentTags.map((tag, idx) => (
                    <Label key={idx} text={tag} type="info" small />
                  ))
                ) : (
                  <span className="text-onlight-text-3 dark:text-ondark-text-3 text-xs">
                    No tags detected
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Triton Inference Panel */}
        <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 border-onlight-line-1 dark:border-ondark-line-1 overflow-hidden rounded-lg border">
          <div className="bg-onlight-bg-2 dark:bg-ondark-bg-2 border-onlight-line-1 dark:border-ondark-line-1 border-b px-4 py-3">
            <h3 className="text-onlight-text-1 dark:text-ondark-text-1 font-medium">
              Triton Inference Server
            </h3>
          </div>
          <div className="p-4">
            <div className="bg-onlight-bg-3 dark:bg-ondark-bg-3 relative aspect-video overflow-hidden rounded-lg">
              {data.tritonVideoUrl ? (
                <video
                  className="h-full w-full object-contain"
                  controls
                  preload="auto"
                  key={data.tritonVideoUrl}
                  autoPlay
                  loop
                >
                  <source src={data.tritonVideoUrl} type="video/mp4" />
                </video>
              ) : (
                <div className="text-onlight-text-2 dark:text-ondark-text-2 flex h-full items-center justify-center">
                  No video available
                </div>
              )}
            </div>
            <div className="mt-3">
              <span className="text-onlight-text-2 dark:text-ondark-text-2 mb-2 block text-xs font-medium">
                Tags
              </span>
              <div className="flex flex-wrap gap-1">
                {data.tritonTags.length > 0 ? (
                  data.tritonTags.map((tag, idx) => (
                    <Label key={idx} text={tag} type="info" small />
                  ))
                ) : (
                  <span className="text-onlight-text-3 dark:text-ondark-text-3 text-xs">
                    No tags detected
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Section */}
      <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 border-onlight-line-1 dark:border-ondark-line-1 rounded-lg border p-6">
        <h3 className="text-onlight-text-1 dark:text-ondark-text-1 mb-4 font-medium">
          {readOnly ? 'Recorded Feedback' : 'Comparison Feedback'}
        </h3>

        <div className="space-y-6">
          {/* Question 1: Bounding Boxes */}
          <div>
            <p className="text-onlight-text-1 dark:text-ondark-text-1 mb-3 text-sm">
              Are the bounding boxes the same in both videos?
            </p>
            <div className="flex gap-3">
              <Button
                variant={displayBoxesSame === true ? 'primary' : 'outline'}
                onClick={() => !readOnly && setBoxesSame(true)}
                disabled={isSubmitted || readOnly}
                className="min-w-[80px]"
              >
                Yes
              </Button>
              <Button
                variant={displayBoxesSame === false ? 'error' : 'outline'}
                onClick={() => !readOnly && setBoxesSame(false)}
                disabled={isSubmitted || readOnly}
                className="min-w-[80px]"
              >
                No
              </Button>
            </div>
          </div>

          {/* Question 2: Tags */}
          <div>
            <p className="text-onlight-text-1 dark:text-ondark-text-1 mb-3 text-sm">
              Are the detected tags the same?
            </p>
            <div className="flex gap-3">
              <Button
                variant={displayTagsSame === true ? 'primary' : 'outline'}
                onClick={() => !readOnly && setTagsSame(true)}
                disabled={isSubmitted || readOnly}
                className="min-w-[80px]"
              >
                Yes
              </Button>
              <Button
                variant={displayTagsSame === false ? 'error' : 'outline'}
                onClick={() => !readOnly && setTagsSame(false)}
                disabled={isSubmitted || readOnly}
                className="min-w-[80px]"
              >
                No
              </Button>
            </div>
          </div>

          {/* Submit */}
          {!readOnly && (
            <div className="border-onlight-line-1 dark:border-ondark-line-1 border-t pt-4">
              {isSubmitted ? (
                <div className="flex items-center gap-2 text-green-600 dark:text-green-500">
                  <CheckCircleIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">
                    Feedback submitted successfully
                  </span>
                </div>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  loading={isSubmitting}
                >
                  Submit Feedback
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ABComparisonView(props: Props) {
  const { locationAlarm, readOnly = false, recordedFeedback, onBack } = props;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [submittedAlarms, setSubmittedAlarms] = useState<Set<string>>(
    new Set()
  );

  const currentRawAlarm = locationAlarm.rawAlarms[currentIndex];
  const currentAlarmId = currentRawAlarm?.id;

  const timestamp = useMemo(
    () => new Date(locationAlarm.alarmTime).getTime(),
    [locationAlarm.alarmTime]
  );

  // Get partition key from alarm time
  const partitionKey = useMemo(() => {
    const date = dayjs.utc(locationAlarm.alarmTime);
    return date.format('YYYYMM');
  }, [locationAlarm.alarmTime]);

  // Fetch inference media for all raw alarms
  const inferenceMediaQuery = useInferenceMedia(locationAlarm.id);

  // Fetch current alarm debug media (with boxes embedded)
  const currentMediaQuery = useAlarmMedia({
    id: currentAlarmId,
    timestamp,
    debug: true,
  });

  // Fetch current alarm details (for tags)
  const currentAlarmQuery = useAlarm(currentAlarmId, partitionKey);

  // Submit feedback mutation
  const feedbackMutation = useABFeedback(() => {
    setSubmittedAlarms((prev) => new Set([...prev, currentAlarmId]));
  });

  // Get inference media item for current alarm
  const inferenceMediaItem = useMemo(() => {
    if (!inferenceMediaQuery.data?.items) return undefined;
    return inferenceMediaQuery.data.items.find(
      (item) => item.alarm_id === currentAlarmId
    );
  }, [inferenceMediaQuery.data, currentAlarmId]);

  // Navigation handlers
  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) =>
      Math.min(locationAlarm.rawAlarms.length - 1, prev + 1)
    );
  }, [locationAlarm.rawAlarms.length]);

  // Submit feedback handler
  const handleSubmitFeedback = useCallback(
    (alarmId: string, boxesSame: boolean, tagsSame: boolean) => {
      feedbackMutation.mutate({
        feature: 'inference_server',
        feedback: {
          location_alarm_id: locationAlarm.id,
          raw_alarm_id: alarmId,
          bounding_boxes_same: boxesSame,
          tags_same: tagsSame,
        },
      });
    },
    [feedbackMutation, locationAlarm.id]
  );

  // Loading state for initial data
  if (inferenceMediaQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <HakimoSpinner />
      </div>
    );
  }

  // Error state
  if (inferenceMediaQuery.isError) {
    return (
      <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 flex h-full items-center justify-center rounded-lg">
        <p className="text-onlight-text-2 dark:text-ondark-text-2">
          Error loading inference media data
        </p>
      </div>
    );
  }

  // No alarms
  if (locationAlarm.rawAlarms.length === 0) {
    return (
      <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 flex h-full items-center justify-center rounded-lg">
        <p className="text-onlight-text-2 dark:text-ondark-text-2">
          No raw alarms found for this location alarm
        </p>
      </div>
    );
  }

  // No inference data available
  if (!inferenceMediaQuery.data?.items?.length) {
    return (
      <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 flex h-full items-center justify-center rounded-lg">
        <p className="text-onlight-text-2 dark:text-ondark-text-2">
          No inference data available for comparison
        </p>
      </div>
    );
  }

  const comparisonData: ComparisonData = {
    alarmId: currentAlarmId,
    currentMedia: currentMediaQuery.data,
    currentMediaLoading: currentMediaQuery.isLoading,
    currentTags: currentAlarmQuery.data?.tags || [],
    currentTagsLoading: currentAlarmQuery.isLoading,
    tritonVideoUrl: inferenceMediaItem?.triton_video_url,
    tritonTags: inferenceMediaItem?.tags || [],
  };

  return (
    <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg flex h-full flex-col">
      {/* Header */}
      <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 border-onlight-line-1 dark:border-ondark-line-1 border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {onBack && (
              <Button variant="outline" onClick={onBack}>
                <ArrowLeftIcon className="mr-1 h-4 w-4" />
                Back
              </Button>
            )}
            <div>
              <h1 className="text-onlight-text-1 dark:text-ondark-text-1 text-lg font-semibold">
                {readOnly ? 'Feedback Review' : 'A/B Test Comparison'}
              </h1>
              <p className="text-onlight-text-2 dark:text-ondark-text-2 mt-1 text-sm">
                Inference Server Comparison for Location Alarm{' '}
                <span className="font-mono">{locationAlarm.id}</span>
              </p>
            </div>
          </div>
          <Label text="Inference Server" type="info" />
        </div>
      </div>

      {/* Navigation Bar - hidden in read-only mode */}
      {!readOnly && (
        <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 border-onlight-line-1 dark:border-ondark-line-1 border-b px-6 py-3">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={currentIndex === 0}
            >
              <ChevronLeftIcon className="mr-1 h-4 w-4" />
              Previous
            </Button>

            <div className="flex items-center gap-4">
              {/* Progress indicators */}
              <div className="flex items-center gap-1.5">
                {locationAlarm.rawAlarms.map((alarm, idx) => (
                  <button
                    key={alarm.id}
                    onClick={() => setCurrentIndex(idx)}
                    className={clsx(
                      'h-2.5 w-2.5 rounded-full transition-colors',
                      idx === currentIndex
                        ? 'bg-primary-600 dark:bg-primary-500'
                        : submittedAlarms.has(alarm.id)
                        ? 'bg-green-500'
                        : 'bg-onlight-line-2 dark:bg-ondark-line-2 hover:bg-onlight-line-3 dark:hover:bg-ondark-line-3'
                    )}
                    title={`Alarm ${idx + 1}${
                      submittedAlarms.has(alarm.id) ? ' (submitted)' : ''
                    }`}
                  />
                ))}
              </div>
              <span className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
                {currentIndex + 1} of {locationAlarm.rawAlarms.length}
              </span>
            </div>

            <Button
              variant="outline"
              onClick={handleNext}
              disabled={currentIndex === locationAlarm.rawAlarms.length - 1}
            >
              Next
              <ChevronRightIcon className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        <AlarmComparisonContent
          key={currentAlarmId}
          data={comparisonData}
          isSubmitted={submittedAlarms.has(currentAlarmId)}
          isSubmitting={feedbackMutation.isLoading}
          onSubmit={handleSubmitFeedback}
          readOnly={readOnly}
          recordedFeedback={recordedFeedback}
        />
      </div>
    </div>
  );
}

export default ABComparisonView;
